import React, { useState, useMemo, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import './ImageViewerListTaskbarStyles.css'
import ProgressBar from 'react-bootstrap/ProgressBar'
import Button from '@components/common/Button/Button.component'
import DicomUploaderModal from '@pages/ImageViewer/DicomUploader/DicomUploader'
import CreateFolderModal from '../ImageViewerFolderList/CreateFolderModal.component'
import { formatBytes } from '@utils/imageViewer.utils'
import {
  createNewFolder,
  fetchTotalStorage,
} from '@store/actions/folder.action'
import { IFolder } from '@dataTypes/folder.types'

type ImageViewerListTaskbarProps = {
  onClickViewFiles: () => void
  selectedFolder: IFolder | null
  childFolders: IFolder[]
  hasSelectedFiles: boolean
}

function ImageViewerListTaskbar({
  onClickViewFiles,
  selectedFolder,
  hasSelectedFiles,
}: ImageViewerListTaskbarProps): React.ReactElement {
  const dispatch = useAppDispatch()
  const [showUploadDicomModal, setShowUploadDicomModal] = useState(false)
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false)

  // Get folder data from Redux store
  const { loading, totalStorage = 0 } = useAppSelector((state) => state.folder)
  const { selectedPatient } = useAppSelector((state) => state.patient)
  const storageLoading = loading

  // Fetch total storage on component mount and when selected patient changes
  useEffect(() => {
    dispatch(fetchTotalStorage())
  }, [dispatch, selectedPatient?.patient_id])

  // Calculate storage usage from total database storage
  const storageInfo = useMemo(() => {
    // Use total storage from all files in the database
    const totalStorageUsed = totalStorage || 0

    // Set storage limit (5GB in bytes)
    const storageLimit = 5 * 1024 * 1024 * 1024 // 5GB in bytes
    const percentage = Math.min((totalStorageUsed / storageLimit) * 100, 100)

    return {
      used: totalStorageUsed,
      limit: storageLimit,
      percentage: percentage,
      formattedUsed: formatBytes(totalStorageUsed),
      formattedLimit: formatBytes(storageLimit),
    }
  }, [totalStorage])

  const handleCreateFolder = (folderName: string): void => {
    if (selectedPatient?.patient_id) {
      // If there's an active selected folder, create the new folder inside it
      const parentPath = selectedFolder?.path || ''

      // If we have a selected folder, use it as parent
      const finalParentFolderId = selectedFolder
        ? selectedFolder.folder_id
        : null

      const folderData = {
        patient_id: selectedPatient.patient_id,
        folder_name: folderName,
        path: parentPath ? `${parentPath}/${folderName}` : `/${folderName}`,
        parent_folder_id: finalParentFolderId,
        folder_size: 0,
      }

      // TEMPORARY: Test with a hardcoded parent folder ID to see if the backend works
      if (finalParentFolderId) {
        const testFolderData = {
          ...folderData,
          parent_folder_id: finalParentFolderId,
        }
        dispatch(createNewFolder(testFolderData))
      } else {
        dispatch(createNewFolder(folderData))
      }
    }
  }

  const handleCloseCreateFolderModal = (): void => {
    setShowCreateFolderModal(false)
  }

  return (
    <div className="taskbar-container">
      <div className="taskbar-back-button">
        <p className="patient-name">
          {selectedPatient?.first_name} {selectedPatient?.last_name}
        </p>
      </div>
      <div className="taskbar-right-container">
        <div className="storage-container">
          <div className="storage-container-top">
            <span className={`mgc_upload_3_fill`}></span>
            <p>Storage used</p>
            <p>
              {storageLoading ? (
                'Loading...'
              ) : (
                <>
                  {storageInfo.formattedUsed}{' '}
                  <span>/ {storageInfo.formattedLimit}</span>{' '}
                  {'(' + Math.round(storageInfo.percentage) + '%)'}
                </>
              )}
            </p>
          </div>

          <ProgressBar now={storageInfo.percentage} />
        </div>

        <Button
          className={`right-taskbar-buttons button-view-files`}
          onClick={onClickViewFiles}
          disabled={!hasSelectedFiles}
        >
          View Files
        </Button>
        <Button
          className={`right-taskbar-buttons button-create-folder`}
          onClick={() => {
            setShowCreateFolderModal(true)
          }}
        >
          Create Folder
        </Button>
        <Button
          size="md"
          className="right-taskbar-buttons button-primary add-contact-button button-upload"
          onClick={() => setShowUploadDicomModal(true)}
        >
          Upload
        </Button>
      </div>
      <DicomUploaderModal
        show={showUploadDicomModal}
        onClose={() => setShowUploadDicomModal(false)}
        selectedFolder={selectedFolder}
      />
      <CreateFolderModal
        show={showCreateFolderModal}
        onHide={handleCloseCreateFolderModal}
        onCreate={handleCreateFolder}
        isLoading={loading}
      />
    </div>
  )
}

export default ImageViewerListTaskbar
